import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Lock,
  Save,
  Sparkles,
} from "lucide-react";
import {
  createTrade,
  formatApiError,
  getPortfolio,
  getQuote,
  listTrades,
  renamePortfolio,
  toastApiError,
} from "@/lib/api";
import { fmtPct, fmtQty, fmtUsd, isCryptoSymbol } from "@/lib/format";

const CHANGE_COLOR = (n) =>
  n === null || n === undefined || Number(n) === 0
    ? "text-[var(--aurion-muted)]"
    : Number(n) > 0
    ? "text-emerald-400"
    : "text-red-400";

export default function PortfolioDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();

  const [p, setP] = useState(null);
  const [trades, setTrades] = useState({ items: [], total: 0 });
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [doc, hist] = await Promise.all([getPortfolio(id), listTrades(id)]);
      setP(doc);
      setNameDraft(doc.name);
      setTrades(hist);
    } catch (e) {
      toastApiError(e);
      nav("/portfolios", { replace: true });
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      await renamePortfolio(id, nameDraft.trim());
      setP({ ...p, name: nameDraft.trim() });
      setEditing(false);
      toast.success("Renamed");
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  if (!p) return <div className="text-[var(--aurion-muted)]">Loading…</div>;

  return (
    <div>
      <Button
        data-testid="back-to-portfolios"
        onClick={() => nav("/portfolios")}
        variant="ghost"
        className="mb-4 pl-0 text-xs uppercase tracking-widest text-[var(--aurion-muted)] hover:bg-transparent hover:text-[var(--aurion-text)]"
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        All portfolios
      </Button>

      {/* Header */}
      <div className="mb-8 flex items-baseline justify-between gap-4">
        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            <Input
              data-testid="rename-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              className="h-11 max-w-sm border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
            />
            <Button
              data-testid="rename-save"
              onClick={save}
              disabled={busy || !nameDraft.trim() || nameDraft.trim() === p.name}
              className="border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </Button>
            <Button
              data-testid="rename-cancel"
              variant="ghost"
              onClick={() => {
                setNameDraft(p.name);
                setEditing(false);
              }}
              className="text-[var(--aurion-muted)]"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              Portfolio
            </div>
            <div className="mt-1 flex items-baseline gap-4">
              <h1
                className="text-3xl text-[var(--aurion-text)]"
                data-testid="portfolio-detail-name"
              >
                {p.name}
              </h1>
              <Button
                data-testid="rename-btn"
                variant="ghost"
                size="sm"
                className="text-xs uppercase tracking-widest text-[var(--aurion-muted)] hover:bg-transparent hover:text-[var(--aurion-gold)]"
                onClick={() => setEditing(true)}
              >
                rename
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SummaryCard
          label="Total equity"
          value={fmtUsd(p.total_equity)}
          testId="stat-equity"
          gold
        />
        <SummaryCard
          label="Cash"
          value={fmtUsd(p.cash)}
          testId="stat-cash"
        />
        <SummaryCard
          label="Total return"
          value={fmtUsd(p.total_return)}
          sub={fmtPct(p.total_return_pct)}
          subClass={CHANGE_COLOR(p.total_return)}
          valueClass={CHANGE_COLOR(p.total_return)}
          testId="stat-total-return"
        />
        <SummaryCard
          label="Realized P&L"
          value={fmtUsd(p.realized_pnl_total)}
          valueClass={CHANGE_COLOR(p.realized_pnl_total)}
          testId="stat-realized-pnl"
        />
      </div>

      <div className="mt-6 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        stocks priced at latest available close (yfinance free tier) · crypto near-real-time
      </div>

      {/* Holdings + Trade ticket */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card
          data-testid="holdings-card"
          className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2 lg:col-span-2"
        >
          <div className="border-b border-[var(--aurion-border)] p-3 text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
            Holdings
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--aurion-border)] hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Symbol
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Qty
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Avg cost
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Price
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Market value
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Unrealized P&L
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="holdings-tbody">
              {p.positions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="p-6 text-center text-sm text-[var(--aurion-muted)]">
                    No positions yet — place your first trade.
                  </TableCell>
                </TableRow>
              ) : (
                p.positions.map((pos) => (
                  <TableRow
                    key={pos.symbol}
                    className="border-[var(--aurion-border)] hover:bg-[var(--aurion-panel-2)]"
                    data-testid={`holding-row-${pos.symbol}`}
                  >
                    <TableCell className="font-mono text-[var(--aurion-text)]">
                      {pos.symbol}
                      {pos.price_stale ? (
                        <Badge variant="outline" className="ml-2 rounded-none border-amber-400/40 text-[9px] uppercase tracking-widest text-amber-300">
                          stale
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[var(--aurion-muted)]">
                      {fmtQty(pos.quantity, pos.symbol)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[var(--aurion-muted)]">
                      {fmtUsd(pos.avg_cost)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[var(--aurion-text)]">
                      {fmtUsd(pos.current_price)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-[var(--aurion-text)]">
                      {fmtUsd(pos.market_value)}
                    </TableCell>
                    <TableCell className={`text-right font-mono text-sm ${CHANGE_COLOR(pos.unrealized_pnl)}`}>
                      <div data-testid={`unrealized-${pos.symbol}`}>{fmtUsd(pos.unrealized_pnl)}</div>
                      <div className="text-[10px] opacity-80">{fmtPct(pos.unrealized_pnl_pct)}</div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        <TradeTicket portfolio={p} onTraded={refresh} />
      </div>

      {/* Trade history */}
      <Card
        data-testid="trades-card"
        className="mt-6 border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2"
      >
        <div className="border-b border-[var(--aurion-border)] p-3 text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
          Trade history · {trades.total}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--aurion-border)] hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">When</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Symbol</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Side</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Qty</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Price</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Value</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Realized</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="trades-tbody">
            {trades.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-6 text-center text-sm text-[var(--aurion-muted)]">
                  No trades yet.
                </TableCell>
              </TableRow>
            ) : (
              trades.items.map((t) => (
                <TableRow
                  key={t.id}
                  className="border-[var(--aurion-border)] hover:bg-[var(--aurion-panel-2)]"
                  data-testid={`trade-row-${t.id}`}
                >
                  <TableCell className="text-[11px] text-[var(--aurion-muted)]">
                    {new Date(t.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-[var(--aurion-text)]">{t.symbol}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`rounded-none text-[10px] uppercase tracking-widest ${
                        t.side === "BUY"
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                          : "border-red-500/40 bg-red-500/10 text-red-300"
                      }`}
                    >
                      {t.side}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[var(--aurion-muted)]">
                    {fmtQty(t.quantity, t.symbol)}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[var(--aurion-muted)]">
                    {fmtUsd(t.price)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--aurion-text)]">
                    {fmtUsd(t.total_value)}
                  </TableCell>
                  <TableCell className={`text-right font-mono text-xs ${CHANGE_COLOR(t.realized_pnl)}`}>
                    {t.realized_pnl === null || t.realized_pnl === undefined
                      ? "—"
                      : fmtUsd(t.realized_pnl)}
                  </TableCell>
                  <TableCell className="text-[10px] uppercase tracking-widest">
                    {t.source === "verdict" && t.analysis_run_id ? (
                      <Link
                        to={`/analyze?run=${t.analysis_run_id}`}
                        data-testid={`trade-source-link-${t.id}`}
                        className="text-[var(--aurion-gold)] hover:underline"
                      >
                        verdict ↗
                      </Link>
                    ) : (
                      <span className="text-[var(--aurion-muted)]">{t.source}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, sub, subClass, valueClass, gold, testId }) {
  return (
    <Card
      data-testid={testId}
      className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-4"
    >
      <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </div>
      <div
        className={`mt-2 font-mono text-2xl ${gold ? "" : valueClass || "text-[var(--aurion-text)]"}`}
        style={gold ? { color: "var(--aurion-gold)" } : undefined}
      >
        {value}
      </div>
      {sub ? (
        <div className={`text-xs ${subClass || "text-[var(--aurion-muted)]"}`}>{sub}</div>
      ) : null}
    </Card>
  );
}

function TradeTicket({ portfolio, onTraded }) {
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("BUY");
  const [entryMode, setEntryMode] = useState("qty"); // qty | usd
  const [qtyStr, setQtyStr] = useState("");
  const [usdStr, setUsdStr] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const autoModeOn = !!portfolio?.desk_settings?.auto_mode;

  const held = useMemo(
    () => portfolio.positions.find((p) => p.symbol === symbol.trim().toUpperCase()) || null,
    [portfolio.positions, symbol]
  );

  const derivedQty = useMemo(() => {
    if (!quote?.price) return 0;
    if (entryMode === "qty") return Number(qtyStr) || 0;
    const q = Number(usdStr) / Number(quote.price);
    return Number.isFinite(q) ? q : 0;
  }, [entryMode, qtyStr, usdStr, quote]);

  const derivedTotal = useMemo(() => {
    if (!quote?.price) return 0;
    return derivedQty * Number(quote.price);
  }, [derivedQty, quote]);

  const fetchQuote = async (raw) => {
    const s = (raw || symbol).trim().toUpperCase();
    if (!s) return;
    setQuoting(true);
    setErr(null);
    try {
      const q = await getQuote(s);
      setQuote(q);
    } catch (e) {
      setQuote(null);
      setErr(formatApiError(e));
    } finally {
      setQuoting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await createTrade(portfolio.id, {
        symbol: symbol.trim().toUpperCase(),
        side,
        quantity: derivedQty,
        source: "manual",
      });
      toast.success(`${side} ${fmtQty(derivedQty, symbol)} ${symbol.toUpperCase()} filled`);
      setQtyStr("");
      setUsdStr("");
      onTraded();
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit =
    !autoModeOn &&
    quote?.price &&
    derivedQty > 0 &&
    derivedTotal >= 1 &&
    (side === "BUY" ? derivedTotal <= portfolio.cash + 0.01 : held && derivedQty <= held.quantity + 1e-6);

  // Inline explanation for a disabled submit button so users don't stare at
  // a dead grey control wondering why nothing happens.
  let disabledHint = null;
  if (autoModeOn) {
    disabledHint =
      "Autopilot is ON for this portfolio — pause Autopilot to trade manually.";
  } else if (quote?.price && derivedQty > 0) {
    if (derivedTotal < 1) {
      disabledHint = `Trade value ${fmtUsd(derivedTotal)} below $1.00 minimum`;
    } else if (side === "BUY" && derivedTotal > portfolio.cash + 0.01) {
      const maxQty = portfolio.cash / Number(quote.price);
      disabledHint = `Insufficient cash — max ${fmtQty(maxQty, symbol)} ${symbol.toUpperCase()}`;
    } else if (side === "SELL" && (!held || derivedQty > held.quantity + 1e-6)) {
      disabledHint = held
        ? `You only hold ${fmtQty(held.quantity, symbol)} ${symbol.toUpperCase()}`
        : `You do not hold ${symbol.toUpperCase()}`;
    }
  }

  return (
    <Card
      data-testid="trade-ticket"
      className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-5 lg:col-span-1"
    >
      <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
        Trade ticket
      </div>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="tt-symbol" className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Symbol
            </Label>
            <Input
              id="tt-symbol"
              data-testid="ticket-symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onBlur={() => fetchQuote()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  fetchQuote();
                }
              }}
              placeholder="AAPL"
              className="mt-1 h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono tracking-wider"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">Side</Label>
            <div className="mt-1 flex overflow-hidden rounded-md border border-[var(--aurion-border)]">
              <button
                type="button"
                data-testid="ticket-buy"
                onClick={() => setSide("BUY")}
                className={`px-4 py-2 text-xs uppercase tracking-widest ${
                  side === "BUY"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "text-[var(--aurion-muted)] hover:bg-[var(--aurion-panel-2)]"
                }`}
              >
                Buy
              </button>
              <button
                type="button"
                data-testid="ticket-sell"
                onClick={() => setSide("SELL")}
                className={`px-4 py-2 text-xs uppercase tracking-widest ${
                  side === "SELL"
                    ? "bg-red-500/15 text-red-300"
                    : "text-[var(--aurion-muted)] hover:bg-[var(--aurion-panel-2)]"
                }`}
              >
                Sell
              </button>
            </div>
          </div>
        </div>

        {quote ? (
          <div
            data-testid="ticket-quote"
            className="flex items-baseline justify-between text-xs"
          >
            <span className="text-[var(--aurion-muted)]">{quote.name}</span>
            <span className="font-mono text-[var(--aurion-gold)]">{fmtUsd(quote.price)}</span>
          </div>
        ) : quoting ? (
          <div className="text-xs text-[var(--aurion-muted)]">Fetching price…</div>
        ) : (
          <div className="text-xs text-[var(--aurion-muted)]">Enter a symbol to fetch price</div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="ticket-entry-qty"
            onClick={() => setEntryMode("qty")}
            className={`flex-1 rounded-md border px-2 py-1 text-[10px] uppercase tracking-widest ${
              entryMode === "qty"
                ? "border-[var(--aurion-gold)]/40 text-[var(--aurion-gold)]"
                : "border-[var(--aurion-border)] text-[var(--aurion-muted)]"
            }`}
          >
            by quantity
          </button>
          <button
            type="button"
            data-testid="ticket-entry-usd"
            onClick={() => setEntryMode("usd")}
            className={`flex-1 rounded-md border px-2 py-1 text-[10px] uppercase tracking-widest ${
              entryMode === "usd"
                ? "border-[var(--aurion-gold)]/40 text-[var(--aurion-gold)]"
                : "border-[var(--aurion-border)] text-[var(--aurion-muted)]"
            }`}
          >
            by $
          </button>
        </div>

        {entryMode === "qty" ? (
          <Input
            data-testid="ticket-qty"
            type="number"
            step={isCryptoSymbol(symbol) ? "0.00000001" : "0.0001"}
            min="0"
            value={qtyStr}
            onChange={(e) => setQtyStr(e.target.value)}
            placeholder="Quantity"
            className="h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
          />
        ) : (
          <Input
            data-testid="ticket-usd"
            type="number"
            step="1"
            min="1"
            value={usdStr}
            onChange={(e) => setUsdStr(e.target.value)}
            placeholder="Dollar amount"
            className="h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
          />
        )}

        <Separator className="bg-[var(--aurion-border)]" />

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              {entryMode === "qty" ? "Est. total" : "Est. quantity"}
            </div>
            <div
              className="mt-1 font-mono text-[var(--aurion-text)]"
              data-testid="ticket-preview"
            >
              {entryMode === "qty"
                ? fmtUsd(derivedTotal)
                : `${fmtQty(derivedQty, symbol)} ${symbol}`}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Available
            </div>
            <div className="mt-1 font-mono text-[var(--aurion-text)]">
              {side === "BUY"
                ? fmtUsd(portfolio.cash)
                : held
                ? `${fmtQty(held.quantity, symbol)} ${symbol}`
                : `0 ${symbol}`}
            </div>
          </div>
        </div>

        {err ? (
          <div data-testid="ticket-error" className="text-sm text-red-400">
            {err}
          </div>
        ) : disabledHint ? (
          <div data-testid="ticket-hint" className="text-xs text-amber-300">
            {disabledHint}
          </div>
        ) : null}

        <Button
          data-testid="ticket-submit"
          type="submit"
          disabled={busy || !canSubmit}
          className={`h-11 w-full border font-semibold tracking-widest ${
            autoModeOn
              ? "border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)]"
              : side === "BUY"
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
              : "border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25"
          }`}
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : autoModeOn ? (
            <Lock className="mr-2 h-4 w-4" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4" />
          )}
          {autoModeOn ? (
            <span data-testid="ticket-autopilot-lock">Autopilot ON</span>
          ) : side === "BUY" ? (
            "Place BUY"
          ) : (
            "Place SELL"
          )}
        </Button>
      </form>
    </Card>
  );
}
